import { type FC, useCallback } from "react";

import { Button, Spacing, Spinner } from "@snapchat/snap-design-system";

import { Flexbox } from "../../shared/components";
import { MAX_SPECTACLES_TAGS } from "../../submit/components/constants";
import { useSpectaclesTags } from "../hooks";

import { SpectaclesTagsContainer } from "./SpectaclesTags.styles";

interface SpectaclesTagsProps {
    onTagClick: (tag: string) => void;
    selectedTags: readonly string[];
}

export const SpectaclesTags: FC<SpectaclesTagsProps> = ({ onTagClick, selectedTags }) => {
    const { spectaclesTags, loading, error } = useSpectaclesTags();

    const handleTagClick = useCallback(
        (tag: string) => {
            // Only add tag if it's not already selected and we haven't reached the limit
            if (!selectedTags.includes(tag) && selectedTags.length < MAX_SPECTACLES_TAGS) {
                onTagClick(tag);
            }
        },
        [selectedTags, onTagClick],
    );

    if (loading) {
        return (
            <SpectaclesTagsContainer>
                <Spinner size="small" />
            </SpectaclesTagsContainer>
        );
    }

    if (error ?? spectaclesTags.size === 0) {
        return null;
    }

    const allTagsSelected = selectedTags.length >= MAX_SPECTACLES_TAGS;

    return (
        <SpectaclesTagsContainer>
            <Flexbox flexWrap="wrap" gap={`${Spacing.XS}px`}>
                {[...spectaclesTags].map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                        <Button
                            key={tag}
                            size="small"
                            type="primary"
                            onClick={() => {
                                handleTagClick(tag);
                            }}
                            isDisabled={isSelected || allTagsSelected}
                            data-testid={`spectacles-tag-${tag}`}
                        >
                            #{tag}
                        </Button>
                    );
                })}
            </Flexbox>
        </SpectaclesTagsContainer>
    );
};
